'use client';

/** @fileoverview Provides scrollable conversation layout and navigation controls. */

import { Button } from '@/components/ui/button';
import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaViewport,
  ScrollBar,
} from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { ArrowDownIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useCallback } from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';

export type ConversationProps = ComponentProps<typeof StickToBottom>;

export const Conversation = ({ className, children, ...props }: ConversationProps) => (
  <StickToBottom
    className={cn('relative flex min-h-0 min-w-0 max-w-full flex-1', className)}
    initial="smooth"
    resize="smooth"
    role="log"
    {...props}
  >
    {(context) => (
      <ScrollArea className="min-h-0 min-w-0 max-w-full flex-1">
        {typeof children === 'function' ? children(context) : children}
        <ScrollBar />
      </ScrollArea>
    )}
  </StickToBottom>
);

export type ConversationContentProps = ComponentProps<typeof StickToBottom.Content>;

export const ConversationContent = ({
  className,
  children,
  // Consumed by the scroll container, not by the content element; dropped to keep it off the DOM.
  scrollClassName: _scrollClassName,
  style,
  ...props
}: ConversationContentProps) => {
  const context = useStickToBottomContext();

  return (
    <ScrollAreaViewport className="min-w-0 max-w-full" ref={context.scrollRef}>
      <ScrollAreaContent
        className={cn('flex w-full min-w-0! max-w-full flex-col gap-8 p-4', className)}
        style={style}
        ref={context.contentRef}
        {...props}
      >
        {typeof children === 'function' ? children(context) : children}
      </ScrollAreaContent>
    </ScrollAreaViewport>
  );
};

export type ConversationScrollButtonProps = ComponentProps<typeof Button>;

export const ConversationScrollButton = ({
  className,
  ...props
}: ConversationScrollButtonProps) => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleScrollToBottom = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  return (
    !isAtBottom && (
      <Button
        className={cn(
          'absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full dark:bg-background dark:hover:bg-muted',
          className
        )}
        onClick={handleScrollToBottom}
        size="icon"
        type="button"
        variant="outline"
        {...props}
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    )
  );
};
